import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaCog, FaGavel, FaInfoCircle, FaRedo, FaStamp } from 'react-icons/fa';

import '../kanunYolu/KanunYolu.css';
import TarihAlani from '../kanunYolu/TarihAlani';
import SureSecimi from '../kanunYolu/SureSecimi';
import AdliTatilSecici from '../kanunYolu/AdliTatilSecici';
import PresetSecici from '../kanunYolu/PresetSecici';
import PresetArama from '../kanunYolu/PresetArama';
import Acilir from '../kanunYolu/Acilir';
import KanunYoluSonuc from '../kanunYolu/KanunYoluSonuc';
import KanunYoluAyarlarModal from '../kanunYolu/KanunYoluAyarlarModal';
import { parseIso } from '../kanunYolu/isGunuHesap';
import { hesapla } from '../kanunYolu/kesinlesmeHesaplayici';
import { diniSetKur, ekBayramlariTemizle } from '../kanunYolu/diniBayramlar';
import { tumPresetler } from '../kanunYolu/presetler';
import { istisnaDegerlendir } from '../kanunYolu/istisnaiDonemler';
import { ayarOku, ayarYaz, birimAdi, presetleriTemizle, VARSAYILAN_SURE } from '../kanunYolu/ayarlar';

// 1–999 dışı / boş süreyi varsayılana (15) indir. Modül seviyesinde → kararlı kimlik.
const cozSure = (raw) => {
  const n = Number(raw);
  return (n >= 1 && n <= 999) ? Math.floor(n) : VARSAYILAN_SURE;
};

// Kullanıcı preset'i için kimlik üret.
const yeniId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `u-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;

function KanunYoluSuresi() {
  const [ayar, setAyar] = useState(() => ayarOku());
  const [tebligIso, setTebligIso] = useState('');
  const [sure, setSure] = useState(() => String(ayar.defaultSure));
  const [birim, setBirim] = useState(() => ayar.defaultBirim);
  const [adliTatil, setAdliTatil] = useState(() => ayar.adliTatil); // 'kapali'|'hmk'|'cmk'
  const [selectedPresetId, setSelectedPresetId] = useState(null);
  const [arama, setArama] = useState('');
  const [aramaAcik, setAramaAcik] = useState(false);
  const [sonuc, setSonuc] = useState(null);
  const [tebligHata, setTebligHata] = useState('');
  const [ayarAcik, setAyarAcik] = useState(false);

  // Etkin dinî bayram kümesi (saf, ekBayramlar değişince yenilenir).
  const diniSet = useMemo(() => diniSetKur(ayar.ekBayramlar), [ayar.ekBayramlar]);
  // Yerleşik + kullanıcı preset'leri.
  const presetListesi = useMemo(() => tumPresetler(ayar.presetler), [ayar.presetler]);
  const seciliPreset = presetListesi.find((p) => p.id === selectedPresetId) || null;
  // Ana ekranda yalnızca favoriler görünür.
  const favoriPresetler = useMemo(
    () => presetListesi.filter((p) => ayar.favoriler.includes(p.id)),
    [presetListesi, ayar.favoriler],
  );
  // Palette gösterilecek liste: arama boşken favoriler; arama varken TÜM presetler
  // içinde (ad/dayanak/rozet, Türkçe-duyarlı) süzülür — favori olmayanlar da bulunur.
  const gosterilecekPresetler = useMemo(() => {
    const q = arama.trim().toLocaleLowerCase('tr');
    if (!q) return favoriPresetler;
    return presetListesi.filter(
      (p) => !p.manuel
        && [p.ad, p.dayanak, p.rozet].some((s) => s && s.toLocaleLowerCase('tr').includes(q)),
    );
  }, [arama, favoriPresetler, presetListesi]);
  const manuelSecili = !!(seciliPreset && seciliPreset.manuel);
  // Süre alanı: ayardan açıksa veya "Manuel" preset seçiliyse her hâlükârda görünür.
  const sureGoster = ayar.showSure || manuelSecili;

  const hesaplaCalistir = useCallback(() => {
    if (!parseIso(tebligIso)) {
      setTebligHata('Lütfen geçerli bir tebliğ tarihi girin (GG/AA/YYYY).');
      setSonuc(null);
      return;
    }
    setTebligHata('');
    const s = cozSure(sure);
    const r = hesapla(tebligIso, s, birim, diniSet, adliTatil);
    const istisnalar = istisnaDegerlendir({
      tebligIso, baslangic: r.baslangic, sonGun: r.sonItiraz, sure: s, birim, diniSet,
    });
    setSonuc({ ...r, istisnalar, girdi: { tebligIso, sure: s, birim, adliTatil } });
  }, [tebligIso, sure, birim, diniSet, adliTatil]);

  // Tam + geçerli tarih girilince otomatik hesapla (ayar açıksa).
  useEffect(() => {
    if (ayar.autoHesap && parseIso(tebligIso)) hesaplaCalistir();
  }, [tebligIso, sure, birim, adliTatil, diniSet, ayar.autoHesap, hesaplaCalistir]);

  const onTebligChange = (iso) => {
    setTebligIso(iso);
    setTebligHata('');
    if (iso === '' && ayar.silinceSifirla) setSonuc(null);
  };

  // Süre/birim/adli tatil ELLE değişince seçili preset bırakılır (Manuel hariç — onda elle giriş beklenir).
  const onSureChange = (v) => { setSure(v); if (!manuelSecili) setSelectedPresetId(null); };
  const onBirimChange = (v) => { setBirim(v); if (!manuelSecili) setSelectedPresetId(null); };
  const onAdliTatilChange = (v) => { setAdliTatil(v); if (!manuelSecili) setSelectedPresetId(null); };

  const onPresetSec = (p) => {
    setArama(''); // seçim yapılınca palet favorilere dönsün
    setAramaAcik(false); // ve arama alanı kapansın
    if (p.manuel) { setSelectedPresetId('manuel'); return; } // süre/birim/rejim korunur, alan açılır
    setSure(String(p.sure));
    setBirim(p.birim);
    setAdliTatil(p.adliTatil);
    setSelectedPresetId(p.id);
  };

  const yeniHesap = () => {
    setTebligIso('');
    setTebligHata('');
    setSonuc(null);
  };

  // --- Ayarlar ---
  const onAyarKaydet = (kismi) => {
    const yeni = { ...ayar, ...kismi };
    setAyar(yeni);
    ayarYaz(yeni);
    // Yalnızca DEĞİŞEN varsayılanları ana ekrana yansıt (yalnız bir toggle değişince sıfırlama olmasın).
    let degisti = false;
    if (yeni.defaultSure !== ayar.defaultSure) { setSure(String(yeni.defaultSure)); degisti = true; }
    if (yeni.defaultBirim !== ayar.defaultBirim) { setBirim(yeni.defaultBirim); degisti = true; }
    if (yeni.adliTatil !== ayar.adliTatil) { setAdliTatil(yeni.adliTatil); degisti = true; }
    if (degisti) setSelectedPresetId(null);
  };

  const onPresetEkle = (ad, s, b, rejim) => {
    const presetler = presetleriTemizle([...ayar.presetler, { id: yeniId(), ad, sure: s, birim: b, adliTatil: rejim }]);
    const yeni = { ...ayar, presetler };
    setAyar(yeni);
    ayarYaz(yeni);
  };

  const onPresetSil = (id) => {
    const presetler = ayar.presetler.filter((p) => p.id !== id);
    const favoriler = ayar.favoriler.filter((x) => x !== id);
    const yeni = { ...ayar, presetler, favoriler };
    setAyar(yeni);
    ayarYaz(yeni);
  };

  const onFavoriTutle = (id) => {
    const favoriler = ayar.favoriler.includes(id)
      ? ayar.favoriler.filter((x) => x !== id)
      : [...ayar.favoriler, id];
    const yeni = { ...ayar, favoriler };
    setAyar(yeni);
    ayarYaz(yeni);
  };

  return (
    <div className="kys mx-auto max-w-2xl">
      <div className="app-card animate-fade-in-up">
        {/* Başlık şeridi (lacivert + altın) */}
        <div className="app-header flex items-center justify-between">
          <div className="relative z-10 flex items-center">
            <div className="app-header-icon">
              <FaStamp className="h-4 w-4" />
            </div>
            <h1 className="app-header-title ml-3">Kanun Yolu Süresi Hesaplama</h1>
          </div>
          <button
            type="button"
            onClick={() => setAyarAcik(true)}
            className="relative z-10 flex h-9 w-9 items-center justify-center rounded-full bg-amber-400/10 text-amber-300 ring-1 ring-amber-300/30 backdrop-blur-sm transition-colors hover:bg-amber-400/20 hover:text-amber-200"
            title="Ayarlar"
            aria-label="Ayarlar"
          >
            <FaCog className="h-4 w-4" />
          </button>
        </div>

        {/* Gövde */}
        <div className="bg-gradient-to-b from-gray-50 to-white p-6 sm:p-7">
          {/* Tebliğ tarihi */}
          <div>
            <label className="app-label">Tebliğ tarihi</label>
            <TarihAlani valueIso={tebligIso} onChange={onTebligChange} />
            {tebligHata ? (
              <p className="mt-1.5 text-xs font-semibold text-rose-600">{tebligHata}</p>
            ) : null}
            <p className="mt-1.5 text-xs text-slate-400">
              Tebligatın yapıldığı gün. Bu gün süreye dâhil değildir.
            </p>
          </div>

          {/* Hazır süreler paleti — başlıkta açılır arama; arama varken tüm presetlerde süzülür */}
          {ayar.hazirSureGoster && favoriPresetler.length ? (
            <div className="mt-5">
              <PresetArama value={arama} onChange={setArama} acik={aramaAcik} onAcikChange={setAramaAcik} />
              {gosterilecekPresetler.length ? (
                <PresetSecici presetler={gosterilecekPresetler} selectedId={selectedPresetId} onSec={onPresetSec} />
              ) : (
                <p className="px-1 py-1.5 text-xs text-slate-400">
                  <span className="font-medium text-slate-500">“{arama.trim()}”</span> için eşleşen hazır süre bulunamadı.
                </p>
              )}
            </div>
          ) : null}

          {/* Kanun yolu süresi (süre alanı açıkken veya Manuel seçiliyken) */}
          {sureGoster ? (
            <div className="mt-5 sm:max-w-xs">
              <label className="app-label">Kanun yolu süresi</label>
              <SureSecimi
                sure={sure}
                birim={birim}
                onSureChange={onSureChange}
                onSureBlur={() => setSure(String(cozSure(sure)))}
                onBirimChange={onBirimChange}
              />
              <p className="mt-1.5 text-xs text-slate-400">
                Varsayılan: {ayar.defaultSure} {birimAdi(ayar.defaultBirim)}
              </p>
            </div>
          ) : null}

          {/* Adli tatil — ana ekran hızlı seçici */}
          <div className="mt-5">
            <label className="app-label">Adli tatil (20 Tem – 31 Ağu)</label>
            <AdliTatilSecici value={adliTatil} onChange={onAdliTatilChange} />
          </div>

          {/* Seçili presetin dönem/başlangıç notu (ör. 1/6/2024 öncesi/sonrası) */}
          {seciliPreset?.not ? (
            <div className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-800 ring-1 ring-inset ring-amber-200/70">
              <FaInfoCircle className="mt-0.5 h-3.5 w-3.5 flex-none text-amber-500" />
              <span>{seciliPreset.not}</span>
            </div>
          ) : null}

          {/* Süre rozeti (süre alanı gizliyken) */}
          {!sureGoster ? (
            <div className="mt-4">
              <span className="app-chip app-chip-accent">
                <FaGavel className="h-3 w-3" />
                {seciliPreset ? `${seciliPreset.ad}: ` : 'Kanun yolu süresi: '}
                {cozSure(sure)} {birimAdi(birim)}
              </span>
            </div>
          ) : null}

          <button type="button" onClick={hesaplaCalistir} className="btn-primary mt-5 w-full">
            Kesinleşme tarihini hesapla
          </button>

          {sonuc ? (
            <>
              <KanunYoluSonuc
                sonuc={sonuc}
                tebligIso={sonuc.girdi.tebligIso}
                sure={sonuc.girdi.sure}
                birim={sonuc.girdi.birim}
                ekBayramlar={ayar.ekBayramlar}
                istisnalar={sonuc.istisnalar}
              />
              <button type="button" onClick={yeniHesap} className="btn-secondary mt-5 w-full">
                <FaRedo className="h-3.5 w-3.5" /> Yeni hesap
              </button>
            </>
          ) : null}

          {/* Mevzuat */}
          <Acilir baslik="İlgili mevzuat — süre hesabı ve adli tatil" className="mt-6">
            <div className="border-t border-slate-100 px-4 pb-4 pt-3 text-sm leading-relaxed text-slate-600">
              <strong className="text-slate-800">Sürelerin hesaplanması (CMK m.39 / HMK m.92-93)</strong>
              <br />
              Gün ile belirlenen süreler tebliğin ertesi günü işlemeye başlar; tebliğ günü süreye dâhil değildir.
              Hafta/ay süreleri, son hafta/ayda isim veya sayı itibarıyla karşılık gelen günde sona erer; o gün
              yoksa ayın son günü. Son gün resmî tatile rastlarsa süre, takip eden ilk iş günü sona erer.
              <br />
              <br />
              <strong className="text-slate-800">Ceza kanun yolu süreleri (7499 sk. — 8. Yargı Paketi)</strong>
              <br />
              İstinaf (CMK m.273), temyiz (CMK m.291) ve itiraz (CMK m.268) süreleri 12.03.2024'te
              yürürlüğe giren 7499 sk. ile <strong className="text-slate-800">iki haftaya</strong> çıkarıldı; başlangıç
              da artık tefhim değil, <strong className="text-slate-800">gerekçeli kararın tebliğidir</strong>. Yeni süre,
              Geçici m.1 gereği <strong className="text-slate-800">1/6/2024 ve sonrasında verilen</strong> kararlara uygulanır;
              daha önceki kararlarda eski süreler (istinaf/itiraz 7 gün, temyiz 15 gün) geçerlidir.
              <br />
              <br />
              <strong className="text-slate-800">Adli tatil (HMK m.104 / CMK m.331 / İYUK m.8/3, m.61)</strong>
              <br />
              Adli tatil <span className="font-display font-semibold text-slate-800">20 Temmuz – 31 Ağustos</span>
              {' '}arasıdır. Sürenin son günü bu döneme rastlarsa süre, adli tatilin bitiminden itibaren{' '}
              <strong className="text-slate-800">HMK ve idari yargı (İYUK m.8/3) alanlarında 1 hafta</strong>,{' '}
              <strong className="text-slate-800">CMK alanlarında 3 gün</strong> uzar.
              Tebligat/süre başlangıcı adli tatil içindeyse süre 1 Eylül'den itibaren işler.
              <br />
              <br />
              <strong className="text-slate-800">İstisnai süre durmaları (COVID-19 ve deprem)</strong>
              <br />
              <strong className="text-slate-800">COVID-19:</strong> 7226 sk. + 2480 s. CK ile yargısal süreler{' '}
              <span className="font-display font-semibold text-slate-800">13/3/2020 – 15/6/2020</span> arasında durdu;
              16/6/2020'den kaldığı yerden işledi (durma başında ≤15 gün kalan süreler 30/6/2020'ye uzadı).{' '}
              <strong className="text-slate-800">2023 depremi:</strong> 120 + 139 s. CBK ile OHAL ilan edilen 10 ilde
              süreler <span className="font-display font-semibold text-slate-800">6/2/2023 – 6/4/2023</span> (bazı yerlerde
              1/5/2023) arasında durdu. Tebliğ/süre tarihi bu dönemlere denk gelirse hesap altında ayrıca bilgilendirilirsiniz.
              <br />
              <span className="text-xs text-slate-500">
                Not: İdari ve vergi dava açma süreleri (İYUK m.7) İYUK m.8/3 gereği adli tatilden etkilenir.
                Yalnızca AYM bireysel başvuru (30 gün) süresi adli tatile tabi değildir.
                Sonuçlar bilgilendirme amaçlıdır; her somut olayda mevzuat ve güncel içtihatla teyit edilmelidir.
              </span>
            </div>
          </Acilir>
        </div>
      </div>

      <KanunYoluAyarlarModal
        isOpen={ayarAcik}
        onClose={() => setAyarAcik(false)}
        ayar={ayar}
        onSave={onAyarKaydet}
        onEkBayramEkle={(tip, ilkGun) => {
          const ekBayramlar = ekBayramlariTemizle([...ayar.ekBayramlar, { tip, ilkGun }]);
          const yeni = { ...ayar, ekBayramlar };
          setAyar(yeni);
          ayarYaz(yeni);
        }}
        onEkBayramSil={(i) => {
          const ekBayramlar = ayar.ekBayramlar.filter((_, idx) => idx !== i);
          const yeni = { ...ayar, ekBayramlar };
          setAyar(yeni);
          ayarYaz(yeni);
        }}
        onPresetEkle={onPresetEkle}
        onPresetSil={onPresetSil}
        onFavoriTutle={onFavoriTutle}
      />
    </div>
  );
}

export default KanunYoluSuresi;
